/**
 * DimensionTuningDialog — the editor for the two scoring knobs quill-engine
 * genuinely reads.
 *
 * The Dimensions page showed `decay_half_life_days` and `importance` read-only
 * from the day it shipped ("a v1 editor is out of scope"), while the backend's
 * PATCH had accepted both all along and nothing ever called it with them. The
 * 2026-08-03 L5 audit found the gap; Grant's decision was to make them
 * editable.
 *
 * They are edited TOGETHER, in one dialog, because they are one decision about
 * one dimension and because a per-cell inline input would make a production
 * write out of a stray keystroke. The dialog follows QuillActionConfirm's
 * visual language (warning icon, Playfair title, Cancel/Confirm footer) and
 * requires a reason, exactly like every other Quill production write.
 *
 * Validation is `dimensionBounds.ts`, which mirrors the route's. The confirm
 * button will not submit an out-of-bounds draft, AND the backend refuses one
 * with 400 DIMENSION_BOUNDS — the dialog is the friendly half, the 400 is the
 * guarantee. A value that has not changed is not sent at all.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { colors, typography } from '../../../theme/designTokens';
import {
  HALF_LIFE_MAX,
  HALF_LIFE_MIN,
  IMPORTANCE_MAX,
  IMPORTANCE_MIN,
  validateDimensionTuning,
} from './dimensionBounds';

export interface DimensionTuningValues {
  decay_half_life_days: number;
  importance: number;
}

export interface DimensionTuningDialogProps {
  open: boolean;
  dimensionKey: string;
  displayName: string;
  current: DimensionTuningValues;
  loading?: boolean;
  /** Called with ONLY the fields that actually changed, plus the reason. */
  onConfirm: (changes: Partial<DimensionTuningValues>, reason: string) => void;
  onCancel: () => void;
}

/** Parse a text field to a number, or NaN — never to 0, which is a real value. */
const toNumber = (raw: string): number => (raw.trim() === '' ? NaN : Number(raw));

const DimensionTuningDialog: React.FC<DimensionTuningDialogProps> = ({
  open,
  dimensionKey,
  displayName,
  current,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [halfLife, setHalfLife] = useState('');
  const [importance, setImportance] = useState('');
  const [reason, setReason] = useState('');

  // Reset to the stored values whenever the dialog (re)opens for a dimension.
  useEffect(() => {
    if (open) {
      setHalfLife(String(current.decay_half_life_days));
      setImportance(String(current.importance));
      setReason('');
    }
    // `current` is read only at open time on purpose: a background refresh must
    // not overwrite what the admin is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dimensionKey]);

  const draft = useMemo(
    () => ({ decay_half_life_days: toNumber(halfLife), importance: toNumber(importance) }),
    [halfLife, importance]
  );
  const errors = useMemo(() => validateDimensionTuning(draft), [draft]);

  const changes: Partial<DimensionTuningValues> = {};
  if (draft.decay_half_life_days !== current.decay_half_life_days) {
    changes.decay_half_life_days = draft.decay_half_life_days;
  }
  if (draft.importance !== current.importance) {
    changes.importance = draft.importance;
  }

  const changed = Object.keys(changes).length > 0;
  const reasonValid = reason.trim().length > 0;
  const canConfirm = changed && reasonValid && Object.keys(errors).length === 0 && !loading;

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontFamily: typography.fonts.display,
        }}
      >
        <TuneIcon sx={{ color: colors.warning.main }} />
        Edit scoring for {displayName}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
          These two values are read by the live Quill engine (production). They do not error when
          they are wrong — they quietly change every persona compile and every interview ranking
          from the next pass onwards.
        </Typography>

        <Box
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: '8px',
            backgroundColor: colors.gray[100],
            fontFamily: typography.fonts.mono,
            fontWeight: 700,
            textAlign: 'center',
            userSelect: 'all',
          }}
        >
          {dimensionKey}
        </Box>

        <TextField
          label="Decay half-life (days)"
          type="number"
          value={halfLife}
          onChange={(e) => setHalfLife(e.target.value)}
          fullWidth
          size="small"
          autoComplete="off"
          sx={{ mt: 2 }}
          error={Boolean(errors.decay_half_life_days)}
          helperText={
            errors.decay_half_life_days ||
            `τ in the evidence-decay curve: after this many days a memory's evidence counts half ` +
              `as much. ${HALF_LIFE_MIN}–${HALF_LIFE_MAX} days.`
          }
          inputProps={{
            'aria-label': `Decay half-life in days for ${dimensionKey}`,
            min: HALF_LIFE_MIN,
            max: HALF_LIFE_MAX,
            step: 1,
          }}
        />

        <TextField
          label="Importance"
          type="number"
          value={importance}
          onChange={(e) => setImportance(e.target.value)}
          fullWidth
          size="small"
          autoComplete="off"
          sx={{ mt: 2 }}
          error={Boolean(errors.importance)}
          helperText={
            errors.importance ||
            `A multiplier on which questions get asked next ` +
              `(priority × coverage gap × importance × fit). ${IMPORTANCE_MIN}–${IMPORTANCE_MAX}, ` +
              'two decimal places.'
          }
          inputProps={{
            'aria-label': `Importance for ${dimensionKey}`,
            min: IMPORTANCE_MIN,
            max: IMPORTANCE_MAX,
            step: 0.01,
          }}
        />

        <TextField
          label="Reason for the change"
          placeholder="Recorded in the change log (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          sx={{ mt: 2 }}
          inputProps={{ 'aria-label': 'Reason for the change' }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(changes, reason.trim())}
          disabled={!canConfirm}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            backgroundColor: colors.warning.main,
            color: '#1B1F23',
            '&:hover': { backgroundColor: colors.warning.dark },
          }}
        >
          {loading ? 'Saving…' : 'Save scoring'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DimensionTuningDialog;
